import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { braids } from "../data/braids";
import {
  colorOptions,
  lengthOptions,
  sizeOptions,
} from "../data/options";
import {
  mensBraidCountOptions,
  mensHairWashPrice,
} from "../data/mensBraidOptions";
import {
  STITCH_CORNROW_PRICE_PER_BRAID,
  stitchCornrowCountOptions,
} from "../data/stitchCornrowOptions";

import BraidCarousel from "../components/BraidCarousel";
import OptionSelector from "../components/OptionSelector";
import PriceSummary from "../components/PriceSummary";
import BookingForm from "../components/BookingForm";

function BraidPage() {
  const { id } = useParams();

  const braid = braids.find((item) => item.id === id);

  const isMensBraid = braid?.category === "mens";

  const [color, setColor] = useState("1");
  const [customColor, setCustomColor] = useState("");
  const [length, setLength] = useState("mid-back");
  const [size, setSize] = useState("medium");
  const [mensBraidCount, setMensBraidCount] = useState("");
  const [hairWash, setHairWash] = useState(false);
  const [stitchBraidCount, setStitchBraidCount] = useState("4");

  const getOptionPrice = (options, value) => {
    const option = options.find((item) => item.value === value);
    return option?.price ?? 0;
  };

  const colorPrice = useMemo(
    () => getOptionPrice(colorOptions, color),
    [color]
  );

  const lengthPrice = useMemo(
    () => getOptionPrice(lengthOptions, length),
    [length]
  );

  const sizePrice = useMemo(
    () => getOptionPrice(sizeOptions, size),
    [size]
  );

  const mensBraidCountPrice = getOptionPrice(
    mensBraidCountOptions,
    mensBraidCount
  );

  const washPrice = hairWash
    ? mensHairWashPrice
    : 0;

  if (!braid) {
    return (
      <main>
        <h1>Braid style not found</h1>
      </main>
    );
  }


  const isStitchCornrows = braid?.id === "stitch-cornrows";
  const stitchCornrowPrice = isStitchCornrows
  ? Number(stitchBraidCount) * STITCH_CORNROW_PRICE_PER_BRAID
  : 0;

const selections = isMensBraid
  ? {
      braidCount: mensBraidCount,
      hairWash,
    }
  : isStitchCornrows
    ? {
        color,
        ...(color === "custom" ? { customColor } : {}),
        length,
        size,
        braidCount: stitchBraidCount,
        hairWash,
      }
    : {
        color,
        ...(color === "custom" ? { customColor } : {}),
        length,
        size,
        hairWash,
      };
  

const canBook = isMensBraid
  ? mensBraidCount !== ""
  : isStitchCornrows
    ? stitchBraidCount !== "" &&
      (color !== "custom" || customColor.trim().length > 0)
    : color !== "custom" || customColor.trim().length > 0;

  const total = isMensBraid
  ? braid.basePrice + mensBraidCountPrice + washPrice
  : isStitchCornrows
    ? stitchCornrowPrice +
      colorPrice +
      lengthPrice +
      sizePrice +
      washPrice
    : braid.basePrice +
      colorPrice +
      lengthPrice +
      sizePrice +
      washPrice;

  return (
    <main className="braid-page">
      <div className="braid-details">
        <BraidCarousel
          images={braid.images}
          braidName={braid.name}
        />

        <div className="braid-info">
          <h1>{braid.name}</h1>

        <p className="base-price">
          {isStitchCornrows
            ? `Starting at $${stitchCornrowCountOptions[0].price}`
            : `Starting at $${braid.basePrice}`}
        </p>

          {isMensBraid ? (
            <OptionSelector
              title="Number of braids"
              options={mensBraidCountOptions}
              value={mensBraidCount}
              onChange={setMensBraidCount}
            />
          ) : (
            <>
              {isStitchCornrows && (
                <OptionSelector
                  title="Number of braids"
                  options={stitchCornrowCountOptions}
                  value={stitchBraidCount}
                  onChange={setStitchBraidCount}
                />
              )}            
              <OptionSelector
                title="Color"
                options={colorOptions}
                value={color}
                onChange={setColor}
                customValue={customColor}
                onCustomChange={setCustomColor}
              />

              <OptionSelector
                title="Length"
                options={lengthOptions}
                value={length}
                onChange={setLength}
              />

              <OptionSelector
                title="Size"
                options={sizeOptions}
                value={size}
                onChange={setSize}
              />
            </>
          )}

          <div className="hair-wash-option">
            <label>
              <input
                type="checkbox"
                checked={hairWash}
                onChange={(event) =>
                  setHairWash(event.target.checked)
                }
              />

              <span>Add hair wash</span>
              <span>+${mensHairWashPrice}</span>
            </label>
          </div>

          <PriceSummary
            basePrice={
              isStitchCornrows
                ? stitchCornrowPrice
                : braid.basePrice
            }
            colorPrice={isMensBraid ? 0 : colorPrice}
            lengthPrice={isMensBraid ? 0 : lengthPrice}
            sizePrice={isMensBraid ? 0 : sizePrice}
            braidCountPrice={
              isMensBraid ? mensBraidCountPrice : 0
            }
            braidCountIsFullPrice={isStitchCornrows}
            washPrice={washPrice}
            total={total}
          />
        </div>
      </div>

      <BookingForm
        braid={braid}
        selections={selections}
        total={total}
        canBook={canBook}
      />
    </main>
  );
}

export default BraidPage;